import { useEffect, useRef, useState } from 'react';
import { getArtistTopTrack } from '../api';
import { openExternal } from '../openExternal';
import { useYoutubeFallbackPlayer } from './useYoutubeFallbackPlayer';

// Every "play a preview" button (leaderboard desktop + phone, profile, Home,
// Discover, Hall of Fame, the heat map popup) plays through here, so pressing
// play either plays something or ends on "No preview available" - never a
// button that silently does nothing (issue #448).
//
// /api/artist-top-track returns ordered `candidates` (own upload -> Deezer ->
// YouTube). When one won't play - the <audio> element errors, play() is
// rejected, or nothing has started after STALL_MS, or the YouTube player
// reports an error or never loads - the next one is tried. Once the list runs
// out the backend is asked again (see lookupMore) before giving up.

// How long an audio preview gets to start before it counts as dead.
export const STALL_MS = 5000;
// How long the YouTube player gets to load. Blocked by an ad blocker or a
// network filter, its script never loads and it reports nothing at all.
export const VIDEO_STALL_MS = 10000;
// Each lookup skips everything tried so far, so one per source (upload,
// Deezer, YouTube) is enough to reach the end of the chain.
export const MAX_LOOKUPS_MORE = 3;
// Deezer signs its preview URLs for ~15 minutes. A list older than this is
// re-fetched with fresh signatures instead of skipping past the failed source.
const STALE_MS = 10 * 60 * 1000;

export const isPlayableTrack = (t) => !!(t && (t.preview_url || t.video_id));

// Responses from before #448 only carry the top-level track.
export function candidatesFrom(result) {
  if (Array.isArray(result?.candidates)) return result.candidates.filter(isPlayableTrack);
  return isPlayableTrack(result) ? [result] : [];
}

// A track that can only be opened on its platform (an artist's featured
// Spotify link). Only ever offered when nothing is playable.
export function linkOutFrom(result) {
  if (Array.isArray(result?.candidates)) return result.link_out?.url ? result.link_out : null;
  return result?.url && !isPlayableTrack(result) ? result : null;
}

// The backend's `skip=` name for a candidate that failed to play.
export function skipKeyOf(track) {
  if (track?.featured) return 'featured';
  if (track?.source === 'own' || track?.source === 'upload') return 'upload';
  return track?.source || null;
}

const sameTrack = (a, b) =>
  !!((a.preview_url && a.preview_url === b.preview_url) || (a.video_id && a.video_id === b.video_id));

export const untried = (candidates, tried) => candidates.filter((c) => !tried.some((t) => sameTrack(c, t)));

// Plays `audio` (switching to `url` first when given) and calls onFail once
// if it errors, play() is rejected, or it hasn't started within STALL_MS.
// A rejection for autoplay policy calls onBlocked instead - the clip is fine,
// it just needs a tap. Returns a function that stops watching (call it on a
// user pause, or nothing may then count as a stall).
export function startAudio(audio, url, { onFail, onBlocked, onStarted } = {}) {
  if (url) audio.src = url;
  let done = false;
  let timer = null;
  const stop = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    audio.removeEventListener('error', fail);
    audio.removeEventListener('playing', started);
  };
  function started() {
    if (done) return;
    stop();
    if (onStarted) onStarted();
  }
  function fail() {
    if (done) return;
    stop();
    if (onFail) onFail();
  }
  timer = setTimeout(fail, STALL_MS);
  audio.addEventListener('error', fail);
  audio.addEventListener('playing', started);
  let attempt;
  try {
    attempt = audio.play();
  } catch (err) {
    attempt = Promise.reject(err);
  }
  Promise.resolve(attempt).then(started, (err) => {
    if (done) return;
    if (err?.name === 'NotAllowedError') {
      stop();
      if (onBlocked) onBlocked();
    } else if (err?.name !== 'AbortError') {
      // AbortError is a pause()/src change interrupting this play() call,
      // not a broken clip; the error event or stall timer still decides.
      fail();
    }
  });
  return stop;
}

// After every candidate failed: one more lookup. A stale list is simply
// re-fetched (fresh Deezer signatures); otherwise the failed sources go in
// `skip`, which also makes the backend drop its cached copies of them.
export async function lookupMore(lookup, tried, fetchedAt) {
  const stale = Date.now() - fetchedAt > STALE_MS;
  const skip = stale ? [] : [...new Set(tried.map(skipKeyOf).filter(Boolean))];
  const result = await getArtistTopTrack({ ...lookup, skip });
  return { candidates: untried(candidatesFrom(result), tried), linkOut: linkOutFrom(result) };
}

// For code outside React (the heat map popup). Plays candidates[from] and
// walks down the list when one fails. onTrack(track) fires for each
// candidate reached - a video candidate is left to the caller to show, since
// its iframe's errors can't be observed from here; onExhausted fires when
// nothing is left. Returns a cancel function.
export function playCandidates(audio, candidates, { from = 0, onTrack, onBlocked, onExhausted } = {}) {
  let cancel = () => {};
  let cancelled = false;
  const tryAt = (i) => {
    if (cancelled) return;
    const track = candidates[i];
    if (!track) {
      if (onExhausted) onExhausted();
      return;
    }
    if (onTrack) onTrack(track, i);
    if (track.preview_url) {
      cancel = startAudio(audio, track.preview_url, { onFail: () => tryAt(i + 1), onBlocked });
    } else {
      audio.pause();
    }
  };
  tryAt(from);
  return () => {
    cancelled = true;
    cancel();
  };
}

const hasAny = (obj) => !!obj && Object.values(obj).some(Boolean);

// state: 'idle' (lookup deferred until the first play) | 'loading' | 'ready'
// | 'playing' | 'none' (nothing playable anywhere).
//
// `lookup` is getArtistTopTrack's params. `sample` ({ url, title }) is the
// artist's own upload, tried before anything is looked up. `fetchOnMount`
// false defers the lookup to the first play (a table of rows shouldn't fire
// one request per row). `autoPlay` starts as soon as something is found.
// onPlay/onStop fire when real playback starts/stops.
export function usePreviewPlayer({
  lookup,
  sample = null,
  fetchOnMount = true,
  autoPlay = false,
  onPlay,
  onStop,
} = {}) {
  const [state, setState] = useState(fetchOnMount || sample?.url ? 'loading' : 'idle');
  const [list, setList] = useState({ candidates: [], index: 0, linkOut: null });
  const [videoActive, setVideoActive] = useState(false);
  const [started, setStarted] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const s = useRef({ gen: 0, candidates: [], index: 0, linkOut: null, tried: [], fetched: false, fetchedAt: 0, lookupsMore: 0 });
  const wantPlay = useRef(false);
  const audioRef = useRef(null);
  const audioSrc = useRef(null);
  const cancelWatch = useRef(() => {});
  const callbacks = useRef({});
  callbacks.current = { onPlay, onStop };
  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;
  const lookupKey = JSON.stringify(lookup || {});

  const track = list.candidates[list.index] || null;
  const yt = useYoutubeFallbackPlayer(videoActive && track?.video_id ? track.video_id : null, { autoplay: true });

  const publish = () => {
    const st = s.current;
    setList({ candidates: st.candidates, index: st.index, linkOut: st.linkOut });
  };

  const getAudio = () => {
    if (!audioRef.current && typeof Audio !== 'undefined') {
      const audio = new Audio();
      audio.addEventListener('play', () => {
        setState('playing');
        setStarted(true);
        callbacks.current.onPlay?.();
      });
      audio.addEventListener('pause', () => {
        setState((st) => (st === 'playing' ? 'ready' : st));
        callbacks.current.onStop?.();
      });
      audio.addEventListener('ended', () => {
        setState('ready');
        setPosition(0);
        callbacks.current.onStop?.();
      });
      audio.addEventListener('timeupdate', () => setPosition(audio.currentTime));
      const onDuration = () => { if (Number.isFinite(audio.duration)) setDuration(audio.duration); };
      audio.addEventListener('loadedmetadata', onDuration);
      audio.addEventListener('durationchange', onDuration);
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  const watchAudio = (url) => {
    cancelWatch.current = startAudio(getAudio(), url, {
      onFail: fail,
      onBlocked: () => setState('ready'),
      onStarted: () => setState((st) => (st === 'loading' ? 'playing' : st)),
    });
  };

  const start = (i) => {
    const st = s.current;
    st.index = i;
    publish();
    cancelWatch.current();
    setStarted(false);
    setPosition(0);
    setDuration(0);
    const t = st.candidates[i];
    if (t.preview_url) {
      setVideoActive(false);
      if (!getAudio()) return;
      audioSrc.current = t.preview_url;
      setState('loading');
      watchAudio(t.preview_url);
    } else {
      audioRef.current?.pause();
      setState('loading');
      setVideoActive(true);
    }
  };

  async function exhausted() {
    const st = s.current;
    const gen = st.gen;
    cancelWatch.current();
    setVideoActive(false);
    if (st.lookupsMore < MAX_LOOKUPS_MORE && hasAny(lookupRef.current)) {
      st.lookupsMore += 1;
      setState('loading');
      let more = { candidates: [], linkOut: null };
      try {
        more = await lookupMore(lookupRef.current, st.tried, st.fetched ? st.fetchedAt : Date.now());
      } catch {
        // fall through to "nothing playable"
      }
      if (gen !== s.current.gen) return;
      st.fetched = true;
      st.fetchedAt = Date.now();
      st.linkOut = more.linkOut || st.linkOut;
      if (more.candidates.length) {
        st.candidates = [...st.candidates, ...more.candidates];
        start(st.index + 1);
        return;
      }
    }
    st.index = st.candidates.length;
    publish();
    setState(st.linkOut ? 'ready' : 'none');
  }

  function fail() {
    const st = s.current;
    const t = st.candidates[st.index];
    if (t) st.tried.push(t);
    if (st.index + 1 < st.candidates.length) start(st.index + 1);
    else exhausted();
  }

  const load = async () => {
    const st = s.current;
    const gen = st.gen;
    setState('loading');
    let result = null;
    try {
      result = await getArtistTopTrack(lookupRef.current || {});
    } catch {
      result = null;
    }
    if (gen !== s.current.gen) return;
    st.fetched = true;
    st.fetchedAt = Date.now();
    st.candidates = candidatesFrom(result);
    st.linkOut = linkOutFrom(result);
    st.index = 0;
    publish();
    if (!st.candidates.length) {
      setState(st.linkOut ? 'ready' : 'none');
      return;
    }
    setState('ready');
    if (wantPlay.current) start(0);
  };

  const sampleUrl = sample?.url || null;
  const sampleTitle = sample?.title || null;
  const sampleDuration = sample?.duration_seconds || null;
  useEffect(() => {
    const st = s.current;
    st.gen += 1;
    cancelWatch.current();
    if (audioRef.current) audioRef.current.pause();
    audioSrc.current = null;
    Object.assign(st, { candidates: [], index: 0, linkOut: null, tried: [], fetched: false, fetchedAt: 0, lookupsMore: 0 });
    setVideoActive(false);
    setStarted(false);
    setPosition(0);
    setDuration(0);
    wantPlay.current = autoPlay;
    if (sampleUrl) {
      st.candidates = [{ preview_url: sampleUrl, url: sampleUrl, name: sampleTitle || 'Untitled track', source: 'own', duration_seconds: sampleDuration }];
      publish();
      setState('ready');
      if (autoPlay) start(0);
    } else if (fetchOnMount) {
      load();
    } else {
      publish();
      setState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey, sampleUrl, sampleTitle]);

  useEffect(() => () => {
    s.current.gen += 1;
    cancelWatch.current();
    if (audioRef.current) audioRef.current.pause();
  }, []);

  // The YouTube player's own state drives ours while a video candidate is up.
  // Its state isn't reset when the video changes, so a leftover 'none' from
  // the previous video is ignored until the new one reports something.
  const ytSeen = useRef({ id: null });
  useEffect(() => {
    const id = videoActive ? track?.video_id : null;
    if (!id) {
      ytSeen.current = { id: null };
      return;
    }
    if (ytSeen.current.id !== id) {
      ytSeen.current = { id };
      if (yt.state === 'none') return;
    }
    if (yt.state === 'playing') {
      setState('playing');
      setStarted(true);
      callbacks.current.onPlay?.();
    } else if (yt.state === 'ready') {
      setState('ready');
      callbacks.current.onStop?.();
    } else if (yt.state === 'none') {
      fail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yt.state, videoActive, track?.video_id]);

  useEffect(() => {
    if (!videoActive || !track?.video_id || state !== 'loading') return undefined;
    const timer = setTimeout(fail, VIDEO_STALL_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoActive, track?.video_id, state]);

  const pause = () => {
    wantPlay.current = false;
    cancelWatch.current();
    if (audioRef.current) audioRef.current.pause();
    yt.pause();
  };

  const toggle = () => {
    if (state === 'loading') return;
    if (state === 'playing') {
      pause();
      return;
    }
    wantPlay.current = true;
    const st = s.current;
    if (!st.fetched && !st.candidates.length) {
      load();
      return;
    }
    const t = st.candidates[st.index];
    if (!t) {
      if (st.linkOut?.url) openExternal(st.linkOut.url);
      return;
    }
    if (t.preview_url) {
      if (audioSrc.current !== t.preview_url) start(st.index);
      else watchAudio(null);
    } else if (!videoActive) {
      setState('loading');
      setVideoActive(true);
    } else {
      yt.play();
    }
  };

  const seek = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = seconds;
    setPosition(seconds);
  };

  const closeVideo = () => {
    yt.pause();
    setVideoActive(false);
    setState('ready');
  };

  return {
    state,
    track,
    linkOut: track ? null : list.linkOut,
    toggle,
    pause,
    seek,
    started,
    position,
    duration,
    yt,
    showVideo: videoActive && !!track?.video_id && (state === 'playing' || state === 'ready'),
    closeVideo,
  };
}

export default usePreviewPlayer;
